import { debounce, some } from 'lodash-es';
import type { MapboxIMap } from '..';
import { loadImages } from '../mapImages';
import { configureMapboxMap, setLastMapPosition } from '../mapboxMapConfig';
import { mapboxStyle } from '../style';
import { updateStyleLayers } from './updateMode';

declare global {
  interface Window {
    __pnMap: mapboxgl.Map; // mapbox-gl instance, set by initialize.ts
  }
}

const debouncedSetLastMapPosition = debounce(setLastMapPosition, 500);

export const initialize: MapboxIMap['initialize'] = async function (
  this: MapboxIMap,
  mapContainer,
  signal,
  options
): Promise<void> {
  if (isMapAlreadyRendered(mapContainer)) {
    console.log('%cSkipping initialization of the map', 'color: #aaa');
    return;
  }

  return new Promise<void>((resolveInit, rejectInit) => {
    signal.addEventListener('abort', () => {
      return rejectInit();
    });

    updateStyleLayers(
      mapboxStyle,
      options.mapMode,
      options.grids,
      options.baseLayers
    ).then((mapboxStyleWithMode) => {
      this._native = configureMapboxMap(
        mapContainer,
        mapboxStyleWithMode,
        options
      );

      const mapHasLoaded = new Promise<void>((resolve) =>
        this._native.on('load', () => {
          loadImages(this._native);
          resolve();
        })
      );
      const styleHasLoaded = new Promise<void>((resolve) =>
        this._native.on('styledata', () => {
          resolve();
        })
      );

      Promise.all([mapHasLoaded, styleHasLoaded]).then(() => {
        this._native.on('move', () => {
          const { lng, lat } = this._native.getCenter();
          debouncedSetLastMapPosition({
            center: [lng, lat],
            zoom: this._native.getZoom(),
          });
        });

        if (import.meta.env.DEV) {
          window.__pnMap = this._native; // used by E2E tests
        }

        setIsDone(true);
        return resolveInit();
      });
    });
  });
};

// HACK:
let isDone = false;
export function setIsDone(state: boolean): void {
  isDone = state;
}

function isMapAlreadyRendered(mapContainer: HTMLElement): boolean {
  return (
    isDone &&
    some(
      Array.from(mapContainer.children).map((childNode) => childNode.className),
      (className) => className.includes('mapboxgl')
    )
  );
}
